import AmplitudeUtils from './amplitude-utils';
import { getSecurityQuestionsPromptToken } from './anet-client';
import {
  isAthenaOneWebClient,
  isAthenaOneWebSso,
  isNonSsoLoginAttempt
} from './app-utils';
import {
  getAthenaOneParamsFromJwt,
  getBannerConfig
} from './banner-jwt-utils';
import {
  clearStorage,
  isSecurityQuestionsTokenCookieSet,
  setSecurityQuestionsPromptCookies
} from './storage-utils';
import { getVanityIssuer } from './widget-facade';
import { processCredsApi } from './iam-core-client';
import { autoSubmitPostForm } from './html-utils';

export const fetchAndStoreSqToken = async () => {
  const response = await getSecurityQuestionsPromptToken();
  if (response?.sqtoken) {
    setSecurityQuestionsPromptCookies({ sqtoken: response.sqtoken });
  }
};

export const getCustomLoginMetadata = (bannerConfig: any) => {
  if (bannerConfig?.bannerType) {
    const metadata = { ...bannerConfig };
    delete metadata.header;
    delete metadata.description;
    delete metadata.alertType;
    return metadata;
  }
  return {
    isSso: !!new URLSearchParams(window.location.search).get('idp'),
  };
};

export const onSuccessfulAuth = async () => {
  const athenaOneParams = await getAthenaOneParamsFromJwt();
  const bannerProps = getBannerConfig(athenaOneParams, isAthenaOneWebSso(athenaOneParams));
  await AmplitudeUtils.logCustomEvent('Success', getCustomLoginMetadata(bannerProps));

  // Fetch and store the security questions JWT from athenaOne
  // only for an athenaOne web non-sso login.
  if (
    isAthenaOneWebClient() &&
    isNonSsoLoginAttempt(athenaOneParams) &&
    !isSecurityQuestionsTokenCookieSet()
  ) {
    await fetchAndStoreSqToken();
  }

  clearStorage();
};

export const processCreds = async (payload: { username: string }) => {
  if (settings.isAuthnWithLwaClientId && settings.callProcessCreds) {
    await processCredsApi({
      ...payload,
      updateUserMfaProfile: !!settings.updateUserMfaProfile,
    });
  }
};

export function getConfigForInteractionCodeFlow(config: any) {
  const requestContext = OktaUtil.getRequestContext();
  // Populate codeChallenge and codeChallengeMethod so the widget calls the globalSuccessFn injected previously:
  // https://github.com/okta/okta-signin-widget/blob/master/src/v2/client/interactionCodeFlow.js#L33
  const authenticationRequest = requestContext?.authentication?.request;
  return {
    ...config,
    codeChallenge: authenticationRequest?.code_challenge,
    codeChallengeMethod: authenticationRequest?.code_challenge_method,
    redirect: 'never',
  };
}


type InteractionCodeFlowResponse = {
  status: string;
  interaction_code: string;
  state: string;
}

export function completeInteractionCodeFlow(res: InteractionCodeFlowResponse) {
  const requestContext = OktaUtil.getRequestContext();
  const authenticationRequest = requestContext?.authentication?.request;
  const responseMode = authenticationRequest?.response_mode;
  const redirectUri = authenticationRequest?.redirect_uri;
  if (redirectUri) {
    if (responseMode === 'form_post') {
      autoSubmitPostForm({
        action: redirectUri,
        params: {
          interaction_code: res.interaction_code,
          state: res.state,
        },
      });
    }
    else {
      const url = new URL(redirectUri);
      url.searchParams.set('interaction_code', res.interaction_code);
      url.searchParams.set('state', res.state);
      window.location.assign(url.toString());
    }
  }

}

function getAuthClientOptions() {
  const requestContext = OktaUtil.getRequestContext();
  const authenticationRequest = requestContext?.authentication?.request;
  return {
    clientId: requestContext?.authentication?.client?.id,
    redirectUri: authenticationRequest?.redirect_uri,
    codeChallenge: authenticationRequest?.code_challenge,
    codeChallengeMethod: authenticationRequest?.code_challenge_method,
    scopes: authenticationRequest?.scope?.trim()?.split(' '),
    responseMode: authenticationRequest?.response_mode,
    responseType: authenticationRequest?.response_type,
    // Issuer must be of vanity domain and not okta domain, so that session exists when
    // making calls to /me endpoints from lwa spa.
    issuer: getVanityIssuer(requestContext),
    state: authenticationRequest?.state,
    flow: 'default',
    // This is required to populate stateHandle during initial introspect call.
    idx: {
      stateHandle: settings.stateHandle,
    },
  };
}

export function setAuthClientOptionsForInteractionCodeFlow(oktaSignIn: any) {
  // Payload passed to the interact endpoint comes from meta which is populated through auth client options
  // https://github.com/okta/okta-auth-js/blob/master/lib/idx/interact.ts#L53
  oktaSignIn.authClient.options = {
    ...oktaSignIn.authClient.options,
    ...getAuthClientOptions(),
  };
}

export function saveIdxTransactionMetadata(oktaSignIn: any) {
  const options = {
    exchangeCodeForTokens: false,
    ignoreSignature: false,
    ...getAuthClientOptions(),
  };
  // Ensure meta and options have similar properties as per checks in
  // https://github.com/okta/okta-auth-js/blob/master/lib/idx/transactionMeta.ts#L110
  oktaSignIn.authClient.idx.saveTransactionMeta(options);
}
